// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Whole documents
// NEVER leave the device — only matched passages + the question go to the brain (see brain.ts).
//
// Data model:
//   document { id, title, kind, body, char_count, key_date_count, created_at, updated_at }
//   key_date { id, doc_id, doc_title, kind, label, date, context }  ← derived on-device in Rust
//   passage  { doc_id, title, kind, page, snippet }                 ← the local retrieval result

import { invoke } from "./tauri-web";

export type DocKind = "lease" | "insurance" | "warranty" | "id" | "utility" | "other";

export interface DocumentMeta {
  id: string;
  title: string;
  kind: DocKind;
  char_count: number;
  key_date_count: number;
  created_at: string;
  updated_at: string;
  /** Downscaled JPEG data-URL of a photo attached to this document, ON-DEVICE only. "" when unset.
   *  The model never sees it — a photo filed through chat is proof/attachment, not vision input. */
  photo: string;
}

export interface Document extends DocumentMeta {
  /** Full extracted plain text — on-device only, never sent in bulk to the brain. */
  body: string;
}

export interface NewDocument {
  title: string;
  kind?: DocKind;
  body: string;
}

/** One key-date ledger row, derived deterministically in Rust at import time. */
export interface KeyDate {
  id: string;
  doc_id: string;
  doc_title: string;
  kind: DocKind;
  /** "renewal" | "expiry" | "notice" | "due" */
  label: KeyDateLabel;
  /** ISO "YYYY-MM-DD". */
  date: string;
  /** Short context snippet the date was lifted from (on-device only). */
  context: string;
}
export type KeyDateLabel = "renewal" | "expiry" | "notice" | "due" | "start";

/** One matched passage from a document — the ONLY document content that leaves the device. */
export interface Passage {
  doc_id: string;
  title: string;
  kind: DocKind;
  /** 1-based approximate page within the document, for citations. */
  page: number;
  snippet: string;
}

/**
 * One pre-ranked headline fact — derived ON-DEVICE in Rust (signals.rs) from the matched documents'
 * money/period figures + key dates. A full sentence carrying figure + unit + a salience tone. These
 * ride alongside the passages on the ONE billed call so the single narration is insightful.
 */
export interface HeadlineFact {
  /** Full English sentence, e.g. "This lease states a deposit of £1,500." */
  text: string;
  /** "urgent" | "notable" | "neutral" — salience tone (proximity / figure size), not advice. */
  tone: "urgent" | "notable" | "neutral";
  /** Descending salience rank (Rust-computed). */
  rank: number;
}

export const store = {
  listDocuments: () => invoke<DocumentMeta[]>("list_documents"),
  getDocument: (id: string) => invoke<Document | null>("get_document", { id }),
  documentCount: () => invoke<number>("document_count"),
  /** Add a document. Rust derives + persists its key dates on-device. */
  addDocument: (input: NewDocument) => invoke<DocumentMeta>("add_document", { input }),
  deleteDocument: (id: string) => invoke<void>("delete_document", { id }),
  /** Attach a photo (downscaled JPEG data-URL) to a document — filed ON-DEVICE from chat. Never
   *  leaves the device, never sent to the model. "" clears it. */
  setDocumentPhoto: (id: string, photo: string) => invoke<void>("set_document_photo", { id, photo }),
  /** Local FTS5 retrieval — the step that runs BEFORE the single brain call. */
  searchPassages: (query: string, limit = 12) =>
    invoke<Passage[]>("search_doc_passages", { query, limit }),
  /**
   * AI-MAX: pre-ranked headline facts for the matched documents (on-device, deterministic). Sent
   * alongside the passages on the ONE billed call so the single narration is insightful. `today` =
   * client-local YYYY-MM-DD so date facts read relatively.
   */
  headlineFactsForDocs: (docIds: string[], today: string, limit = 6) =>
    invoke<HeadlineFact[]>("headline_facts_for_docs", { docIds, today, limit }),
  /** Upcoming deadlines from the on-device ledger. `today` = client local YYYY-MM-DD. */
  upcomingKeyDates: (today: string, limit = 50) =>
    invoke<KeyDate[]>("upcoming_key_dates", { today, limit }),
  allKeyDates: (limit = 200) => invoke<KeyDate[]>("all_key_dates", { limit }),
};

// ── Pure policy / presentation helpers ─────────────────────────────────────────

export const KIND_LABELS: Record<DocKind, string> = {
  lease: "Lease / tenancy",
  insurance: "Insurance",
  warranty: "Warranty",
  id: "ID / personal",
  utility: "Utility / bills",
  other: "Other",
};

/** A small emoji per kind — calm, not loud. */
export const KIND_EMOJI: Record<DocKind, string> = {
  lease: "🏠",
  insurance: "🛡️",
  warranty: "🧾",
  id: "🪪",
  utility: "💡",
  other: "📄",
};

export const ALL_KINDS: DocKind[] = ["lease", "insurance", "warranty", "id", "utility", "other"];

export const LABEL_TEXT: Record<KeyDateLabel, string> = {
  renewal: "Renews",
  expiry: "Expires",
  notice: "Notice by",
  due: "Due",
  start: "Starts",
};

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Pretty calendar date for display, e.g. "1 Sep 2026". */
export const fmtDate = (iso: string): string => {
  const d = toLocalDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Whole days from today (local) to an ISO date. Negative = past. Deterministic, client-side. */
export function daysUntil(iso: string, today = new Date()): number {
  const target = toLocalDate(iso);
  if (!target) return 0;
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const b = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** Human "in N days" / "today" / "N days ago" copy for a key date. */
export function relativeDateCopy(iso: string): string {
  const n = daysUntil(iso);
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  if (n > 1) return `in ${n} days`;
  return `${Math.abs(n)} days ago`;
}

/** Urgency bucket for colour — soonest deadlines warmest. */
export function urgency(iso: string): "soon" | "near" | "later" | "past" {
  const n = daysUntil(iso);
  if (n < 0) return "past";
  if (n <= 14) return "soon";
  if (n <= 60) return "near";
  return "later";
}
